/**
 * Inverts an Object and returns its corresponding values as keys with types.
 *
 * @param object Object to invert
 * @returns Inverted object
 */
export function invertObject<TKey extends string, TVal extends number | string>(
  object: Record<TKey, TVal>
): Readonly<Record<TVal, TKey>> {
  const invertedObject: Record<TVal, TKey> = {} as Record<TVal, TKey>;
  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      const value = object[key];
      invertedObject[value] = key;
    }
  }

  return invertedObject;
}

/**
 * TS type for product name
 */
export type Product =
  | 'Stryktipset'
  | 'Europatipset'
  | 'Matchen'
  | 'Bomben'
  | 'Maltipset'
  | 'TopptipsetStryk'
  | 'TopptipsetExtra'
  | 'Topptipset'
  | 'Powerplay'
  | 'Sportkrysset'
  | 'Fulltraff'
  | 'Challenge';

/**
 * TS type for sub product
 */
export type SubProduct = 'EmTipset' | 'VmTipset';

/**
 * Object mapping between product id and ITS product id
 */
export const ITS_PRODUCT_ID = {
  Stryktipset: 'STRYK',
  Europatipset: 'ITA',
  Matchen: 'MATCHEN',
  Bomben: 'BOMBEN',
  Maltipset: 'MAL',
  TopptipsetStryk: 'T8STRYK',
  TopptipsetExtra: 'T8EXTRA',
  Topptipset: 'T8',
  Powerplay: 'POWERPLAY',
  Sportkrysset: 'SPORTJOKER',
  Fulltraff: 'ANTMAL',
  Challenge: 'TMIX',
} as const satisfies Record<Product, string>;

export type ITSProductId = (typeof ITS_PRODUCT_ID)[keyof typeof ITS_PRODUCT_ID];

/**
 * Object mapping between product id and numeric product id
 */
const COUPON_PRODUCT_MAP_VARIANTS = {
  Stryktipset: 1,
  Europatipset: 2,
  Matchen: 6,
  Bomben: 7,
  Maltipset: 8,
  TopptipsetStryk: 23,
  TopptipsetExtra: 24,
  Topptipset: 25,
  Powerplay: 28,
  Fulltraff: 38,
  Challenge: 45,
} as const satisfies Record<Exclude<Product, 'Sportkrysset'>, number>;

export const PRODUCT_MAP_VARIANTS = {
  ...COUPON_PRODUCT_MAP_VARIANTS,
  Sportkrysset: 30,
} as const satisfies Record<Product, number>;
export type ProductMapVariants = typeof PRODUCT_MAP_VARIANTS;

/**
 * Object mapping between numeric product id and product id
 */
export const PRODUCT_ID_MAP_VARIANTS = invertObject<Product, number>(PRODUCT_MAP_VARIANTS);

/**
 * Object mapping between product id and product names
 */
export const PRODUCT_NAME = {
  Stryktipset: 'Stryktipset',
  Europatipset: 'Europatipset',
  Matchen: 'Matchen',
  Bomben: 'Bomben',
  Maltipset: 'Måltipset',
  TopptipsetStryk: 'Topptipset Stryk',
  TopptipsetExtra: 'Topptipset Extra',
  Topptipset: 'Topptipset',
  Powerplay: 'Powerplay',
  Sportkrysset: 'Sportkryss',
  Fulltraff: 'Fullträff',
  Challenge: 'Challenge',
} as const satisfies Record<Product, string>;

/**
 * Resolves the ITS product id to its numeric product id.
 *
 * @param itsProduct - The ITS product id to resolve.
 * @returns The numeric product id or undefined if not found.
 */
export function resolveITSProduct(itsProduct?: ITSProductId): number | undefined {
  const mapping: Record<ITSProductId, number> = {
    STRYK: 1,
    ITA: 2,
    MATCHEN: 6,
    BOMBEN: 7,
    MAL: 8,
    T8STRYK: 23,
    T8EXTRA: 24,
    T8: 25,
    POWERPLAY: 28,
    SPORTJOKER: 30,
    ANTMAL: 38,
    TMIX: 45,
  };
  return itsProduct ? mapping[itsProduct] : undefined;
}

export const productName: Record<number, string> = {
  1: 'Stryktipset',
  2: 'Europatipset',
  6: 'Matchen',
  7: 'Bomben',
  8: 'Maltipset',
  23: 'Topptipset Stryk',
  24: 'Topptipset Extra',
  25: 'Topptipset',
  28: 'Powerplay',
  38: 'Fullträff',
  45: 'Challenge',
};
